import { faExpand, faFloppyDisk, faImage, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TrashIcon } from "@heroicons/react/24/solid"
import { Node } from "@tiptap/core"
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react"
import classNames from "classnames"
import hotkeys from "hotkeys-js"
import { Transaction } from "prosemirror-state"
import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import { v4 as uuidv4 } from "uuid"

import type { uuid } from "../../../../../../model/types"
import api from "../../../../../../services/api"
import ImageStore, { ImageState } from "./image/store"

export interface ImageOptions {
  HTMLAttributes: Record<string, any>
}
const selectionClasses = "border-2 dark:border-sky-800 -mb-1"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    image: {
      setImage: (options: { uuid: uuid; pos?: number }) => ReturnType
      deleteImageAt: (pos: number) => ReturnType
    }
  }
}

const AddImageBlock = ({
  uuid,
  imageState,
  selected,
}: {
  uuid: uuid
  imageState: ImageState
  selected: boolean
}) => {
  const [dragging, setDragging] = useState<boolean>(false)

  const handleNewFile = (file: File) =>
    store.parseImageFile(file, (result) => {
      if (result.error) store.setImageState(uuid, { type: "empty", error: result.error })
      else if (result.blob) store.gotFile(uuid, result.blob, file)
    })

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) handleNewFile(e.target.files[0])
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleNewFile(e.dataTransfer.files[0])
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      data-drag-handle
      className={classNames(
        " flex justify-stretch items-stretch gap-3 p-4 text-sm rounded border text-gray-500 border-gray-300 dark:text-gray-600 dark:border-gray-700 ",
        {
          "border-2 border-dashed border-white": dragging,
          [selectionClasses]: selected,
        }
      )}
    >
      <FontAwesomeIcon icon={faImage} className="text-lg text-gray-300" />
      <div className="flex-grow flex flex-col">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="file:cursor-pointer"
        />
        {imageState.type == "empty" && imageState.error ? (
          <div className="mt-2 mb-0 text-red-500 text-xs font-medium">{imageState.error}</div>
        ) : (
          <p
            className="mt-2 mb-0 text-xs font-medium text-gray-500 dark:text-gray-300"
            id="file_input_help"
          >
            PNG, JPG or GIF (Max size 5mb).
          </p>
        )}
      </div>
    </div>
  )
}

const uuidsFromContent = (content: string): uuid[] => {
  // console.log("uuidsFromContent", { content })
  if (!content) return []
  const imageUuids = content
    .match(/uuid="([^"]*)"/g)
    ?.map((s) => s.replace('uuid="', "").replace('"', ""))
  return imageUuids || []
}

export const store = new ImageStore()

export const fetchImageSignedUrlsFromContent = (content: string): void =>
  store.fetchImageSignedUrlsFromUuids(uuidsFromContent(content))

// shows modal view that fills the screen, has close button, escape key also closes. Has no state just props
const FullscreenView = ({
  uuid,
  imageState,
  close,
}: {
  uuid: uuid
  imageState: ImageState
  close: () => void
}) => {
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!imageRef.current) return
    if (imageState["blob"] && !imageRef.current.attributes["src"]) {
      const url = URL.createObjectURL(imageState["blob"])
      imageRef.current.onload = () => {
        URL.revokeObjectURL(url)
      }
      imageRef.current.src = url
    }
  }, [imageState])

  useEffect(() => {
    if (!imageRef.current) return
    if (imageState.type == "with-url") {
      api()
        .post("/api/files/urls", {
          uuids: [uuid],
          version: "original",
        })
        .then(({ data }) => {
          if (!imageRef.current || data.files.length === 0) return
          imageRef.current.src = data.files[0].url
        })
    }
  }, [imageState, imageRef.current])

  useEffect(() => {
    hotkeys("escape", "image-fullscreen", function (event, handler) {
      event.preventDefault()
      close()
    })

    return () => {
      hotkeys.unbind("escape", "image-fullscreen")
    }
  }, [])

  hotkeys.setScope("image-fullscreen")

  return (
    <div
      className="z-30 fixed top-0 bottom-0 left-0 right-0 dark:bg-white bg-gray-700 bg-opacity-90 flex justify-center items-center cursor-pointer"
      onClick={() => close()}
    >
      <div className="fixed top-5 right-5 mx-auto text-gray-400 text-xs font-medium">
        Click outside image or press escape to close
      </div>
      <div
        className="m-8 flex justify-center items-center"
        style={{ height: "calc(100vh - 6rem)", width: "calc(100vw - 2rem)" }}
      >
        {imageState.type == "with-blob" && (
          <img
            className="p-0 m-0 max-h-full max-w-full object-contain cursor-auto "
            ref={imageRef}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {imageState.type == "with-url" && (
          <img
            className="p-0 m-0 max-h-full max-w-full object-contain cursor-auto "
            ref={imageRef}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </div>
  )
}

export const ImageView = ({
  uuid,
  selected,
  editable,
  onOpenFullScreen,
  onCloseFullScreen,
  onDelete,
}: {
  uuid: uuid
  selected: boolean
  editable: boolean
  onOpenFullScreen?: () => void
  onCloseFullScreen?: () => void
  onDelete?: () => void
}) => {
  const imageState = useSyncExternalStore(store.subscribeTo(uuid), store.imageStateFetcherFor(uuid))

  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!imageRef.current) return
    if (imageState["blob"] && !imageRef.current.attributes["src"]) {
      const url = URL.createObjectURL(imageState["blob"])
      imageRef.current.onload = () => {
        URL.revokeObjectURL(url)
      }
      imageRef.current.src = url
    }
  }, [imageState])

  const [fullScreenView, setFullScreenView] = useState<boolean>(false)
  return (
    <div className="group relative">
      {fullScreenView && (
        <FullscreenView
          uuid={uuid}
          imageState={imageState}
          close={() => {
            setFullScreenView(false)
            onCloseFullScreen?.()
          }}
        />
      )}
      {(imageState.type == "fetching" || imageState.type == "needs-check") && (
        <div className="p-3 flex justify-stretch items-stretch  text-gray-300 dark:text-gray-600 text-sm dark:hover:bg-gray-700 rounded border border-gray-300 dark:border-gray-700">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {imageState.type == "with-url" && (
        <div
          className={classNames("p-2 flex justify-stretch items-stretch cursor-pointer", {
            [selectionClasses]: selected,
          })}
          data-drag-handle
        >
          <img src={imageState.signedUrl.toString()} className="p-0 m-0" />
        </div>
      )}
      {imageState.type == "with-blob" && (
        <div
          className={classNames("p-2 flex justify-stretch items-stretch cursor-pointer relative", {
            [selectionClasses]: selected,
          })}
          data-drag-handle
        >
          <img ref={imageRef} className="p-0 m-0" />
          {imageState.error && (
            <div className="absolute top-0 bottom-0 left-0 right-0 bg-white border border-black p-1 text-red-500">
              {imageState.error}
            </div>
          )}
        </div>
      )}
      {imageState.type == "with-blob" && imageState["saving"] && (
        <div className="text-gray-100 absolute top-0 left-0 mt-3 ml-3 bg-gray-400 border shadow-sm rounded cursor-pointer flex justify-center items-center w-6 h-6">
          <FontAwesomeIcon icon={faFloppyDisk} spin className="w-4 h-4" />
        </div>
      )}
      {imageState.type == "empty" && editable && (
        <AddImageBlock uuid={uuid} imageState={imageState} selected={selected} />
      )}
      {imageState.type == "empty" && !editable && (
        <div
          className={classNames(
            " flex justify-stretch items-stretch gap-3 p-4 text-sm rounded border text-gray-500 border-gray-300 dark:text-gray-600 dark:border-gray-700 "
          )}
        >
          <FontAwesomeIcon icon={faImage} className="text-lg text-gray-300" />
          No image
        </div>
      )}
      <div className="hidden group-hover:flex text-gray-100 absolute top-0 left-0 right-0 mt-3 mx-3 justify-between pointer-events-none">
        {imageState.type == "with-blob" || imageState.type == "with-url" ? (
          <div className="bg-gray-100 text-gray-800 dark:text-gray-200 dark:bg-gray-700 p-1 px-2 border shadow-sm rounded max-w-lg text-xs whitespace-nowrap overflow-ellipsis">
            {imageState.filename}
          </div>
        ) : (
          <div></div>
        )}
        <div className="flex gap-2">
          {(imageState.type == "with-blob" || imageState.type == "with-url") && (
            <div
              onClick={() => {
                setFullScreenView(true)
                onOpenFullScreen?.()
              }}
              className="bg-gray-400 dark:text-gray-200 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-500 hover:text-gray-300 p-1 border shadow-sm rounded cursor-pointer w-7 h-7 overflow-hidden flex justify-center items-center pointer-events-auto"
            >
              <FontAwesomeIcon icon={faExpand} className="w-3 h-3" />
            </div>
          )}
          {onDelete && (
            <div
              onClick={() => {
                onDelete?.()
              }}
              className="bg-gray-400 dark:text-gray-200 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-500 hover:text-gray-300 p-1 border shadow-sm rounded cursor-pointer w-7 h-7 overflow-hidden flex justify-center items-center pointer-events-auto"
            >
              <TrashIcon className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ImageComponent: React.FC = ({ node, selected, getPos, editor }: any) => {
  const uuid = node.attrs.uuid
  // console.log("Component state: ", imageState)

  const select = () => {
    editor.commands.setNodeSelection(getPos())
  }

  return (
    <NodeViewWrapper onClick={() => select()}>
      <ImageView
        {...{
          uuid,
          selected,
          editable: true,
          onCloseFullScreen: () => {
            hotkeys.setScope("idea-pane")
            editor.commands.focus()
          },
          onOpenFullScreen: () => {
            editor.commands.blur()
          },
          onDelete: () => {
            editor.commands.deleteImageAt(getPos())
          },
        }}
      />
    </NodeViewWrapper>
  )
}

export const Image = Node.create<ImageOptions>({
  name: "image",
  draggable: true,
  atom: true,
  inline: false,
  group: "block",

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      uuid: {
        default: null,
        parseHTML: (element) => element.getAttribute("uuid"),
      },
      error: { default: null },
      file: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: "img",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", { uuid: HTMLAttributes.uuid }]
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          const attributes = {
            uuid: options.uuid || uuidv4(),
          } as any

          // if (options?.blob && options?.file)
          //   store.gotFile(attributes.uuid, options.blob, options.file)

          // if (!options?.blob && !options?.file)
          //   store.setImageState(attributes.uuid, { type: "empty" })

          if (options?.pos)
            return commands.insertContentAt(options.pos, {
              type: this.name,
              attrs: attributes,
            })
          else
            return commands.insertContent({
              type: this.name,
              attrs: attributes,
            })
        },
      deleteImageAt:
        (pos) =>
        ({ tr }: { tr: Transaction }) => {
          tr.delete(pos, pos + 1)
          return true
        },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageComponent)
  },
})
